import { EditOutlined } from '@ant-design/icons';
import { Box, Button, Dialog, Stack } from '@mui/material';
import VenueDto, { initializeVenueTypeOptions } from 'common/dto-types/venue-dto';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { SyncFdcpForm } from 'components/sync-fdcp-form';
import DeleteButton from 'pages/calendar/delete-button';
import { useMemo, useState } from 'react';
import { Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { PopupTransition } from '../../components/@extended/Transitions';
import { dispatch, useSelector } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import {
    closeFetchFdcpForm,
    closeForm,
    openCreateForm,
    openFetchFdcpForm,
    setVenue,
    setVenueColumnsPersistence,
    setVenueListPersistence,
} from '../../store/reducers/venue';
import { deleteVenue, fetchVenueList, fetchVenueTypes, synchronizeFdcpStadiums } from './api';
import VenueTableColumnConfig, { SearchableVenueTableColumnConfig } from './venue-column-config';
import VenueForm from './venue-form';

const VenueListPage = () => {
    const [venueList, setVenueList] = useState<Array<VenueDto>>([]);
    const [pageCount, setPageCount] = useState(0);
    const savedFilters = useSelector((state) => state.venue.filters);
    const savedSorting = useSelector((state) => state.venue.sorting);
    const savedPagination = useSelector((state) => state.venue.pagination);
    const defaultVisibleColumnConfig = useSelector((state) => state.venue.visibleColumns);

    const defaultColumnConfig = useMemo(
        () => [
            ...VenueTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => (
                    <>
                        <IconButton
                            shape="rounded"
                            onClick={() => {
                                dispatch(setVenue(row.row.original as VenueDto));
                                dispatch(openCreateForm());
                            }}
                        >
                            <EditOutlined />
                        </IconButton>
                        <DeleteButton
                            deleteCallback={() => {
                                const venue = row.row.original as any;
                                return deleteVenue(venue.id);
                            }}
                            refreshCallback={async () =>
                                await fetchData({
                                    pageSize: savedPagination.pageSize,
                                    pageIndex: savedPagination.pageIndex,
                                    filters: savedFilters,
                                    searchTerm: savedSorting.searchTerm,
                                    sortBy: savedSorting.sortBy,
                                    sortingType: savedSorting.sortingType,
                                })
                            }
                        ></DeleteButton>
                    </>
                ),
            },
        ],
        [],
    );

    const isFormOpen = useSelector((state) => state.venue.isFormOpen);
    const isFetchFdcpFormOpen = useSelector((state) => state.venue.isFetchFdcpFormOpen);

    const fetchData: BaseTableFetchFuncProps = async ({
        pageSize,
        pageIndex,
        filters,
        searchTerm,
        sortBy,
        sortingType,
    }) => {
        const data = await fetchVenueList({
            pageIndex,
            pageSize,
            sortBy,
            sortingType,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableVenueTableColumnConfig)
                    : undefined,
        });

        const venueTypes = await fetchVenueTypes();
        if (data) {
            if (venueTypes !== null && venueTypes) {
                data.elements.forEach((venue: VenueDto) => {
                    venue.venueType = venueTypes.find((venueType) => venueType.id === venue.venueTypeId);
                });
                initializeVenueTypeOptions(venueTypes);
            }

            const newPageCount = Math.ceil(data.total / pageSize);
            setVenueList(data.elements);
            setPageCount(newPageCount);

            dispatch(
                setVenueListPersistence({
                    filters: filters,
                    sorting: { searchTerm, sortBy, sortingType },
                    pagination: { pageSize, pageIndex },
                }),
            );

            if (data.total > 0 && data.elements.length === 0) {
                await fetchData({
                    pageSize: pageSize,
                    pageIndex: newPageCount - 1,
                    sortingType: sortingType,
                    sortBy: sortBy,
                    filters: filters,
                    searchTerm: searchTerm,
                });
            }
        }
    };

    const handleFetchFdcpStadiumsSubmit = async (tournamentId: string) => {
        const response = await synchronizeFdcpStadiums(tournamentId);
        dispatch(closeFetchFdcpForm());
        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Synchronize successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );

            await fetchData({
                pageSize: savedPagination.pageSize,
                pageIndex: savedPagination.pageIndex,
                filters: savedFilters,
                searchTerm: savedSorting.searchTerm,
                sortBy: savedSorting.sortBy,
                sortingType: savedSorting.sortingType,
            });
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: response?.data || 'Failed to synchronize FDCP stadiums',
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
    };

    const handleColumnVisibilityChange = async (newVisibleColumns: string[]) => {
        dispatch(setVenueColumnsPersistence(newVisibleColumns));
    };

    const refreshVenueList = async () => {
        await fetchData({
            pageSize: savedPagination.pageSize,
            pageIndex: savedPagination.pageIndex,
            filters: savedFilters,
            searchTerm: savedSorting.searchTerm,
            sortBy: savedSorting.sortBy,
            sortingType: savedSorting.sortingType,
        });
        dispatch(closeForm());
    };

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2 }}>
                    <Button
                        sx={{ mr: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={() => dispatch(openCreateForm())}
                    >
                        Add Venue
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            dispatch(openFetchFdcpForm());
                        }}
                    >
                        Synchronize Stadiums from FDCP
                    </Button>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={venueList}
                        fetchData={fetchData}
                        pageCount={pageCount}
                        defaultFilters={savedFilters ?? []}
                        defaultPageSize={savedPagination.pageSize}
                        defaultPageIndex={savedPagination.pageIndex}
                        defaultSortingType={savedSorting.sortingType}
                        defaultSearchTerm={savedSorting.searchTerm}
                        defaultColumnIdToSortBy={savedSorting.sortBy}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        onColumnsVisibilityChange={handleColumnVisibilityChange}
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <VenueForm refreshCallback={() => refreshVenueList()} />
                </Dialog>
            )}
            {isFetchFdcpFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFetchFdcpFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <SyncFdcpForm
                        label="Synchronize stadiums of a Tournament from FDCP"
                        onSubmit={handleFetchFdcpStadiumsSubmit}
                        closeFdcpForm={() => dispatch(closeFetchFdcpForm())}
                    />
                </Dialog>
            )}
        </MainCard>
    );
};

export default VenueListPage;
